import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface OpenGraphProtocolFaq {
  question: string;
  answer: string;
}

interface OpenGraphProtocolWordpressProps {
  page_title?: string;
  faqs?: OpenGraphProtocolFaq[];
}

interface TagRow {
  tag: string;
  does: string;
  source: string;
  mistake: string;
}

const TAG_ROWS: TagRow[] = [
  {
    tag: "og:title",
    does: "Required. The title of the object as it should appear in the graph.",
    source:
      "WordPress core does not print it. A theme can echo the post title from wp_head. Yoast's Open Graph spec uses a Facebook title, then the social title template, then the SEO title, then the SEO title template, then %%title%% %%page%% %%sep%% %%sitename%%. Rank Math's Open Graph guide calls og:title essential, and the Social tab can set a custom title. That guide does not publish a fallback order past the Social tab. AIOSEO's per-post Facebook settings include a Facebook Title, and its troubleshooting sample includes og:title. OG Pilot prints og:title from the configured title, or the post title when that string is empty, on supported singular content when Open Graph output is on and Meta Ownership is not External SEO plugin.",
    mistake: "Putting the site name in the title and in og:site_name, or letting two plugins print two titles.",
  },
  {
    tag: "og:description",
    does: "Optional. A one to two sentence description of the object.",
    source:
      "WordPress core does not print it. A theme can echo the manual excerpt. Yoast uses a Facebook description, then the social description template, then the meta description, then that template, then the excerpt on posts or the term description. Rank Math's Open Graph guide describes og:description as a short description, and the Social tab can set one. That guide does not publish a fallback order. AIOSEO's per-post Facebook settings include a Facebook Description, and its troubleshooting sample includes og:description. OG Pilot prints og:description from the configured description, or the excerpt when that string is empty. Import Existing Social Fields copies a description into overrides. A placeholder such as the excerpt fills the generated card, which is not the same as this meta tag.",
    mistake: "Expecting the post body to become the share text when the tag is empty.",
  },
  {
    tag: "og:image",
    does: "Required. An absolute http or https image URL that represents the object.",
    source:
      "WordPress core does not print it. A featured image is an img until wp_head prints the attachment URL. Yoast uses a Facebook image, then the featured image on posts, then a prominent content image, then the social default, then the site fallback. Rank Math's order is the Social-tab OpenGraph image, then the featured image, then the first image in the post, then the default OpenGraph thumbnail. The guide recommends 1200 by 630. AIOSEO's default image source can be the default image, featured image, attached image, first image in content, a custom field, the author image, or the first available image, with the Default Post Facebook Image as the fallback. A post can also set Image Source to Custom Image. OG Pilot prints the generated image URL, or the featured image when no generated URL is ready. It does not print a processing placeholder. The API JSON field is url. MCP generate_og_image returns image_url.",
    mistake: "A relative path, or two plugins both printing og:image.",
  },
  {
    tag: "og:url",
    does: "Required. The canonical URL, used as the object's permanent ID in the graph.",
    source:
      "Core's rel_canonical on wp_head is a link element, not this property. A theme can print the permalink. Yoast uses the canonical URL, or the series root on paginated content. Rank Math's Open Graph guide says it inserts the canonical URL. AIOSEO's troubleshooting sample includes og:url with the post URL. OG Pilot prints og:url as the permalink on supported singular content when Open Graph output is on.",
    mistake: "A staging URL, or a URL that is not the permalink you shared.",
  },
  {
    tag: "og:type",
    does: "Required. ogp.me says an unmarked page is treated as website. article is the global type for an article.",
    source:
      "WordPress core does not print it. Yoast uses website on the homepage, profile on user profiles, and article on pages, posts, and other templates. Rank Math's guide says the plugin generates og:type from the content, and it describes website for the homepage, profile, article, and product. AIOSEO's troubleshooting sample uses article on a post, and per-post Facebook settings include a Facebook Object Type. OG Pilot prints article for a post or a blog_post template, product for a product, and website otherwise.",
    mistake: "Using article on the homepage, or inventing a type the protocol does not list.",
  },
  {
    tag: "og:site_name",
    does: "Optional. The name of the larger site, not the post title.",
    source:
      "WordPress core stores the name as the site title and does not print this property. A theme can echo the site title. Yoast prints the site name on pages where Open Graph output is on. Rank Math's guide says the content should be the website name. AIOSEO's troubleshooting sample includes og:site_name. OG Pilot prints og:site_name from the site name on supported singular content when Open Graph output is on.",
    mistake: "Repeating the post title here.",
  },
  {
    tag: "og:image:width and og:image:height",
    does: "Optional structured properties. Pixel size of that og:image. Put them after the image they describe. A later og:image starts a new image.",
    source:
      "A theme can use the width and height from wp_get_attachment_image_src. Yoast prints them only when an og:image is set and the width and height are known. Rank Math's Open Graph guide documents og:image:width and og:image:height, and it says to upload images 1200 pixels wide and 630 pixels tall. AIOSEO's aioseo_thumbnail_size filter changes the image size used for og:image and defaults to fullsize. That filter doc does not say AIOSEO prints these tags. OG Pilot prints og:image:width 1200 and og:image:height 630 when it prints an image URL. The API JSON response includes the same width and height.",
    mistake: "Numbers that do not match the file, or attaching them to the wrong image.",
  },
  {
    tag: "og:image:alt",
    does: "Optional. A description of what is in the image, not a caption. ogp.me says a page with og:image should also set this.",
    source:
      "A theme can echo the attachment alt. Yoast's Open Graph functional spec does not list this tag. Rank Math's Open Graph guide documents og:image:alt. AIOSEO's troubleshooting sample does not include it, and the AIOSEO docs linked here do not say whether AIOSEO prints the tag. OG Pilot prints og:image:alt as Social preview image for plus the title when it prints an image URL.",
    mistake: "Using the post title as the alt when it does not describe the picture.",
  },
  {
    tag: "twitter:card",
    does: "Not an Open Graph property. It selects an X card layout. summary_large_image is the large-image card.",
    source:
      "WordPress core does not print it. A theme can echo it next to twitter:image. Yoast's X tag spec defaults twitter:card to summary_large_image when those tags are on. Rank Math lists Summary Card with Large Image, Summary Card, App Card, and Player Card, set from the Social Twitter tab. That guide does not name a default card value. AIOSEO's troubleshooting sample does not include twitter:card. OG Pilot prints twitter:card as summary_large_image when Twitter output is on.",
    mistake: "A summary card when you wanted the large image, or a twitter:image that does not match og:image.",
  },
];

const HEAD_SAMPLE = `<meta property="og:title" content="Hello World" />
<meta property="og:description" content="A one or two sentence description." />
<meta property="og:image" content="https://cdn.ogpilot.com/..." />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta property="og:image:alt" content="What the image shows" />
<meta property="og:url" content="https://example.com/hello-world/" />
<meta property="og:type" content="article" />
<meta property="og:site_name" content="Example" />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:image" content="https://cdn.ogpilot.com/..." />`;

const THEME_SAMPLE = `<?php
// Theme example. WordPress core does not print these tags.
// Remove this hook when Yoast, Rank Math, AIOSEO, or the OG Pilot
// plugin already writes Open Graph tags. Two writers duplicate them.
// Yoast uses og:type website on the homepage. This sample only runs
// on singular posts and pages, and it marks those as article.
add_action( 'wp_head', function () {
    if ( ! is_singular() ) {
        return;
    }

    $post_id = get_queried_object_id();
    $permalink = get_permalink( $post_id );
    if ( ! $permalink ) {
        return;
    }

    echo '<meta property="og:title" content="' . esc_attr( get_the_title( $post_id ) ) . '" />' . "\\n";
    echo '<meta property="og:type" content="article" />' . "\\n";
    echo '<meta property="og:url" content="' . esc_url( $permalink ) . '" />' . "\\n";
    echo '<meta property="og:site_name" content="' . esc_attr( get_bloginfo( 'name' ) ) . '" />' . "\\n";

    $description = get_post_field( 'post_excerpt', $post_id );
    if ( is_string( $description ) && $description !== '' ) {
        echo '<meta property="og:description" content="' . esc_attr( wp_strip_all_tags( $description ) ) . '" />' . "\\n";
    }

    if ( ! has_post_thumbnail( $post_id ) ) {
        return;
    }

    $image = wp_get_attachment_image_src( get_post_thumbnail_id( $post_id ), 'full' );
    if ( ! $image ) {
        return;
    }

    $image_url = esc_url( $image[0] );
    echo '<meta property="og:image" content="' . $image_url . '" />' . "\\n";
    echo '<meta property="og:image:width" content="' . (int) $image[1] . '" />' . "\\n";
    echo '<meta property="og:image:height" content="' . (int) $image[2] . '" />' . "\\n";

    $alt = get_post_meta( get_post_thumbnail_id( $post_id ), '_wp_attachment_image_alt', true );
    if ( is_string( $alt ) && $alt !== '' ) {
        echo '<meta property="og:image:alt" content="' . esc_attr( $alt ) . '" />' . "\\n";
    }

    echo '<meta name="twitter:card" content="summary_large_image" />' . "\\n";
    echo '<meta name="twitter:image" content="' . $image_url . '" />' . "\\n";
} );`;

const API_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json
Accept: application/json

{"token":"YOUR_JWT_TOKEN"}

Required JWT claims: iss (verified domain), sub (API key prefix).
If iat is present, the image cache is valid for 24 hours from that
timestamp. Omit iat and the cache does not expire on its own.
Prefer an official SDK. Docs: https://ogpilot.com/docs

JSON response. Put url in og:image. width and height match the
structured image tags. The template canvas is 1200 by 630.
{
  "id": "s_abc123",
  "status": "completed",
  "url": "https://cdn.ogpilot.com/...",
  "width": 1200,
  "height": 630,
  "created_at": "2024-01-15T12:00:00Z",
  "cached": false,
  "stale": false
}`;

const MCP_CONFIG_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}`;

const MCP_CALL_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Transport: Streamable HTTP
Auth: OAuth 2.1 with PKCE
Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/your-post" }

Optional style. Default is auto-detect.
Returns image_url. Put that URL in og:image.
generate_og_image needs a signed-in account with an active plan
and at least one credit. A trial is not enough.`;

const pluginInstallUrl = "https://wordpress.org/plugins/og-pilot-dynamic-open-graph-images/";

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function OpenGraphProtocolWordpress({
  page_title: pageTitle = "Open Graph Protocol WordPress",
  faqs = [],
}: OpenGraphProtocolWordpressProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                WordPress tags
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                Open graph protocol WordPress
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                The <strong>open graph protocol wordpress</strong> question is about the tags in the head, not a second
                image format. <code>og:title</code>, <code>og:description</code>, <code>og:image</code>, and{" "}
                <code>og:url</code> describe the page to a crawler. WordPress core does not print them. A theme{" "}
                <code>wp_head</code> hook or a plugin does. This page is the explainer. Which path should own the image
                stays on the{" "}
                <Link href="/wordpress" className={linkClass}>
                  WordPress lander
                </Link>
                .
              </p>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                The queries open graph tags wordpress and wordpress open graph meta tags are the same job.{" "}
                <code>og:type</code>, <code>og:site_name</code>, <code>og:image:width</code>,{" "}
                <code>og:image:height</code>, <code>og:image:alt</code>, and <code>twitter:card</code> sit next to the
                four required-or-usual tags. <code>twitter:card</code> is not an Open Graph property.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                  <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
                    Install WordPress plugin
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/sign_up">Get an API key</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/docs/mcp">Install MCP</Link>
                </Button>
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">What each tag does</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Definitions are from{" "}
              <a href="https://ogp.me/" className={linkClass} target="_blank" rel="noreferrer">
                ogp.me
              </a>
              . ogp.me lists <code>og:title</code>, <code>og:type</code>, <code>og:image</code>, and <code>og:url</code>{" "}
              as the four required properties. <code>og:description</code> and <code>og:site_name</code> are optional.
              Plugin columns below cite the vendor doc linked under this table, or the OG Pilot plugin&apos;s tag
              writer.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[56rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Tag</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">What it does</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      Where WordPress gets the value
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Common mistake</th>
                  </tr>
                </thead>
                <tbody>
                  {TAG_ROWS.map((row) => (
                    <tr key={row.tag} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.tag}</th>
                      <td className="px-3 py-3">{row.does}</td>
                      <td className="px-3 py-3">{row.source}</td>
                      <td className="px-3 py-3">{row.mistake}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Yoast&apos;s hierarchy is the{" "}
              <a
                href="https://developer.yoast.com/features/opengraph/functional-specification/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph functional spec
              </a>
              . The current help article labels the editor{" "}
              <a
                href="https://yoast.com/help/custom-open-graph-tags/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Social media appearance
              </a>
              . Rank Math&apos;s{" "}
              <a
                href="https://rankmath.com/kb/open-graph-meta-tags/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph guide
              </a>{" "}
              and{" "}
              <a href="https://rankmath.com/kb/titles-and-meta/" className={linkClass} target="_blank" rel="noreferrer">
                Titles and Meta
              </a>{" "}
              are the Rank Math sources, plus the{" "}
              <a
                href="https://rankmath.com/kb/how-to-add-twitter-cards/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Twitter card guide
              </a>
              . AIOSEO&apos;s sample is the{" "}
              <a
                href="https://aioseo.com/docs/troubleshooting-problems-with-sharing-content-on-facebook/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Facebook troubleshooting doc
              </a>
              . Image choices are{" "}
              <a
                href="https://aioseo.com/docs/setting-a-default-image-for-facebook/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                the default Facebook image doc
              </a>
              . The title and description fields are{" "}
              <a
                href="https://aioseo.com/docs/configuring-the-facebook-settings-for-your-content/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Facebook settings for a post
              </a>
              . The size filter is{" "}
              <a
                href="https://aioseo.com/docs/aioseo_thumbnail_size/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                aioseo_thumbnail_size
              </a>
              . Yoast hooks <code>wp_head</code> from its{" "}
              <a
                href="https://github.com/Yoast/wordpress-seo/blob/trunk/src/integrations/front-end-integration.php"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                front-end integration
              </a>
              , including Twitter presenters when that setting is on.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">See what a page actually outputs</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>Open the public permalink. A preview inside wp-admin is not what the crawler fetches.</li>
              <li>
                View source and search for <code>og:</code>. Tags belong in <code>&lt;head&gt;</code>.{" "}
                <code>wp_head</code> is the{" "}
                <a
                  href="https://developer.wordpress.org/reference/hooks/wp_head/"
                  className={linkClass}
                  target="_blank"
                  rel="noreferrer"
                >
                  action
                </a>{" "}
                themes and plugins use to print them.
              </li>
              <li>
                Count the writers. AIOSEO&apos;s troubleshooting doc shows an HTML comment before its block and tells
                you to keep searching for a second <code>OG:</code> block from the theme or another plugin.
              </li>
              <li>
                Paste the same permalink into the{" "}
                <Link href="/open-graph-debugger" className={linkClass}>
                  Open Graph debugger
                </Link>
                . That checker is not Meta, X, LinkedIn, or Slack. It reads the tags this page is about.
              </li>
            </ol>
            <CodeSample label="Raw tags in head. The image URL is absolute https." code={HEAD_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              A theme hook, only when nothing else writes the tags
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Do not add this hook while Yoast, Rank Math, AIOSEO, or the OG Pilot plugin is already printing Open Graph
              tags. Two writers produce duplicate tags. The{" "}
              <a
                href="https://developer.wordpress.org/themes/functionality/featured-images-post-thumbnails/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                theme handbook
              </a>{" "}
              describes featured images as pictures a theme prints with <code>the_post_thumbnail()</code> after{" "}
              <code>add_theme_support(&apos;post-thumbnails&apos;)</code>. It does not document <code>og:image</code>.{" "}
              <a
                href="https://developer.wordpress.org/reference/functions/rel_canonical/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                rel_canonical
              </a>{" "}
              can print a <code>link</code> canonical. That is not <code>og:url</code>.
            </p>
            <CodeSample
              label="Minimal wp_head sample. Delete it when an SEO plugin or OG Pilot owns the tags."
              code={THEME_SAMPLE}
            />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              <code>get_permalink()</code> is absolute. It is https only when the site is. A relative path can look fine
              in the editor and fail in a unfurl. The description in this sample is the manual excerpt. It does not
              invent one from the post body.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Duplicate tags</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              ogp.me says that when one property is repeated, the first tag from top to bottom is given preference
              during conflicts. That is the protocol rule for arrays. Treat a second <code>og:image</code> as a bug you
              can see in view source.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              If Yoast, Rank Math, or AIOSEO should keep the tags, the OG Pilot plugin FAQ says to set Meta Ownership to
              External SEO plugin so OG Pilot stops outputting social meta tags. Then delete the theme hook. Import
              Existing Social Fields copies title, description, and image values from those plugins into OG Pilot
              overrides. That copy is not a reason to leave both writers on.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">A generated image fills og:image</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The pixels and the tag are different. The generator returns a URL. Something in <code>wp_head</code> has
              to put that URL in <code>og:image</code>. On a WordPress site, install{" "}
              <a href={pluginInstallUrl} className={linkClass} target="_blank" rel="noreferrer">
                OG Pilot Dynamic Open Graph Images
              </a>{" "}
              from the{" "}
              <Link href="/wordpress" className={linkClass}>
                WordPress lander
              </Link>
              . The listing says the plugin uses OAuth with PKCE, so you do not paste a long-lived API secret into the
              theme, and it outputs Open Graph and X/Twitter tags including <code>og:image</code> and{" "}
              <code>twitter:image</code>. When Open Graph output is on, the plugin prints <code>og:title</code>,{" "}
              <code>og:description</code>, <code>og:type</code>, <code>og:url</code>, <code>og:site_name</code>, and{" "}
              <code>og:locale</code>. When an image URL is present it also prints <code>og:image</code>,{" "}
              <code>og:image:url</code>, <code>og:image:secure_url</code> on https, <code>og:image:width</code>,{" "}
              <code>og:image:height</code>, <code>og:image:type</code> when the type is known, and{" "}
              <code>og:image:alt</code>. When Twitter output is on it prints <code>twitter:card</code>{" "}
              <code>summary_large_image</code>, plus title, description, and URL, and with an image it prints{" "}
              <code>twitter:image</code>, <code>twitter:image:src</code>, and <code>twitter:image:alt</code>, then{" "}
              <code>twitter:domain</code> and <code>twitter:site</code> when a site handle is set. The WordPress.org
              changelog for 0.1.0 notes <code>og:image:url</code>, <code>twitter:image:src</code>, and{" "}
              <code>twitter:domain</code>. ogp.me defines <code>og:image:url</code> as identical to{" "}
              <code>og:image</code>.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Outside the plugin, sign the request on a server you control. The JSON <code>url</code> is the value for{" "}
              <code>og:image</code>. Without <code>Accept: application/json</code>, the API docs say the response
              redirects to the CDN URL. Changing the image itself, featured image versus an SEO-plugin field versus this
              generated card, is{" "}
              <Link href="/change-og-image-wordpress" className={linkClass}>
                how to change the OG image in WordPress
              </Link>
              .
            </p>
            <CodeSample label="Public API. The url field is the og:image content." code={API_SAMPLE} />
            <CodeSample
              label="MCP install. Claude and ChatGPT do not read this file. Do not add command, args, or a bearer token."
              code={MCP_CONFIG_SAMPLE}
            />
            <CodeSample label="MCP. generate_og_image returns image_url for og:image." code={MCP_CALL_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              The{" "}
              <a href={pluginInstallUrl} className={linkClass} target="_blank" rel="noreferrer">
                plugin FAQ
              </a>{" "}
              says most images are ready within 5 to 10 minutes depending on queue load and the site traffic that
              triggers WP-Cron. The plan catalog lists Starter at $5/month ($50/year, 10,000-image quota), Builder at
              $49/month ($99/year, 100,000-image quota), and Growth at $99/month ($199/year, unlimited image quota).
              Those defaults are on{" "}
              <Link href="/#pricing" className={linkClass}>
                pricing
              </Link>
              . A Stripe image_quota feature can replace the quota. The catalog has no per-image price. The API docs
              require a verified domain via DNS TXT. The plugin FAQ says the WordPress OAuth flow does not add that
              manual DNS step.
            </p>
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key, install MCP, or use the plugin</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              The tags above are the protocol. An API key signs the CDN URL you place in og:image. MCP lets an agent
              call generate_og_image. The WordPress plugin writes that URL for posts, pages, and WooCommerce.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install MCP</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
                  Install WordPress plugin
                </a>
              </Button>
            </div>
          </section>

          <ActivationGuide
            title="One writer, then check the permalink"
            links={[
              {
                href: "/wordpress",
                label: "WordPress plugin",
                detail: "Install the plugin for posts, pages, and WooCommerce.",
              },
              {
                href: "/change-og-image-wordpress",
                label: "Change the OG image",
                detail: "Featured image, SEO-plugin social image, or a generated card.",
              },
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Sign the CDN URL from a server that is not the WordPress plugin.",
              },
              {
                href: "/docs/mcp",
                label: "Install MCP",
                detail: "Call generate_og_image and put image_url in og:image.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm a single og:image before a platform caches the page.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, the PHP SDK, and the JSON url field.",
              },
            ]}
          >
            <p>
              After the tags are live, run the URL through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . The plugin path is{" "}
              <Link href="/wordpress" className={linkClass}>
                /wordpress
              </Link>
              . Changing the file is{" "}
              <Link href="/change-og-image-wordpress" className={linkClass}>
                /change-og-image-wordpress
              </Link>
              .
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                Open graph protocol wordpress questions
              </h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
